import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { Users, FileText, GraduationCap, Zap, Crown } from "lucide-react";
import { OwlMascot } from "@/components/OwlMascot";

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
        setStats(s.data);
        setUsers(u.data);
      } catch (err) {
        console.error("Admin dashboard fetch failed:", err);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="rounded-3xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-600 p-8 text-white relative overflow-hidden">
          <div className="glow-blob bg-yellow-300 w-56 h-56 -top-10 -right-10" />
          <div className="relative flex items-center justify-between">
            <div>
              <div className="text-yellow-100 uppercase tracking-widest text-xs font-bold mb-2 flex items-center gap-2">
                <Crown className="h-3.5 w-3.5" /> Super Admin
              </div>
              <h1 className="font-outfit font-black text-4xl sm:text-5xl" data-testid="admin-welcome">Owner Console</h1>
            </div>
            <div className="hidden lg:block"><OwlMascot size={130} /></div>
          </div>
        </div>

        {stats && (
          <div className="mt-8 grid md:grid-cols-4 gap-4">
            <Stat testid="admin-stat-users" icon={<Users className="h-5 w-5" />} label="Users" value={stats.users} />
            <Stat testid="admin-stat-reports" icon={<FileText className="h-5 w-5" />} label="Reports" value={stats.reports} />
            <Stat testid="admin-stat-classes" icon={<GraduationCap className="h-5 w-5" />} label="Classes" value={stats.classes} />
            <Stat testid="admin-stat-paid" icon={<Zap className="h-5 w-5" />} label="Paid users" value={stats.paid_users} />
          </div>
        )}

        <div className="mt-10">
          <h2 className="font-outfit font-bold text-2xl text-slate-900 mb-4">All users</h2>
          <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500 font-bold">
                <tr>
                  <th className="text-left px-6 py-3">Name</th>
                  <th className="text-left px-6 py-3">Email</th>
                  <th className="text-left px-6 py-3">Role</th>
                  <th className="text-left px-6 py-3">Billing</th>
                  <th className="text-left px-6 py-3">Reports</th>
                  <th className="text-left px-6 py-3">XP</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-slate-200 text-sm" data-testid={`admin-user-row-${u.user_id}`}>
                    <td className="px-6 py-3 font-semibold text-slate-900">{u.name}</td>
                    <td className="px-6 py-3 font-mono text-slate-600">{u.email}</td>
                    <td className="px-6 py-3">
                      <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${u.role === "owner" ? "bg-amber-100 text-amber-700" : u.role === "teacher" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{u.role}</span>
                    </td>
                    <td className="px-6 py-3 font-mono text-xs text-slate-600">{u._sub?.kind}</td>
                    <td className="px-6 py-3 font-mono">{u.reports_count}</td>
                    <td className="px-6 py-3 font-mono">{u.xp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

const Stat = ({ icon, label, value, testid }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={testid}>
    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center border border-amber-200">{icon}</div>
    <div className="mt-3 text-3xl font-outfit font-black text-slate-900">{value}</div>
    <div className="text-sm text-slate-500 uppercase tracking-widest font-bold">{label}</div>
  </div>
);

export default AdminDashboard;
